import { ElementType } from "domelementtype";
import parse, {
  DOMNode,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import { augmentUrl } from "./augmentUrl";
import { createProxyUrl } from "./createUrls";
import { isPopupNeededForUrl } from "./externalUrl";

type Navigate = ((url: string) => void) | null | undefined;
interface HtmlReactParserOptions {
  aemBaseUrl: string;
  locale: string;
  setPopupUrl: React.Dispatch<React.SetStateAction<string>>;
  setIsPopupVisible: React.Dispatch<React.SetStateAction<boolean>>;
  navigate?: Navigate;
}

const generateOptions = (
  aemBaseUrl: string,
  locale: string,
  setPopupUrl: React.Dispatch<React.SetStateAction<string>>,
  setIsPopupVisible: React.Dispatch<React.SetStateAction<boolean>>,
  navigate?: Navigate,
): HTMLReactParserOptions => {
  const options: HTMLReactParserOptions = {
    replace: (element: DOMNode) => {
      const { type, name, attribs, children } = element as Element;

      if (type === ElementType.Tag && name === "a" && attribs?.href) {
        const href = attribs.href;

        if (isPopupNeededForUrl(href, locale)) {
          // @ts-expect-error Library types don't expose onclick
          attribs.onClick = (e: Event) => {
            e.preventDefault();
            setPopupUrl(createProxyUrl(href));
            setIsPopupVisible(true);
          };
        }

        if (shouldUseNavigate(navigate, href)) {
          // @ts-expect-error Library types don't expose onclick
          attribs.onClick = (e: Event) => {
            e.preventDefault();
            navigate!(href);
          };
        }

        attribs.href = augmentUrl(href, aemBaseUrl);
      }

      return {
        type,
        name,
        attribs,
        children,
      } as Element;
    },
  };

  return options;
};

export const htmlReactParser = (
  source: string,
  options: HtmlReactParserOptions = {
    aemBaseUrl: "",
    locale: "en-us",
    setPopupUrl: () => null,
    setIsPopupVisible: () => null,
  },
) => {
  const { aemBaseUrl, navigate, locale, setIsPopupVisible, setPopupUrl } =
    options;
  const htmlReactParserOptions = generateOptions(
    aemBaseUrl,
    locale,
    setPopupUrl,
    setIsPopupVisible,
    navigate,
  );

  return parse(source, htmlReactParserOptions);
};

export const removeAllHtmlTags = (str: string) =>
  str.replaceAll(/<\/?[a-z\d-]{0,50}>/gi, "");

export const shouldUseNavigate = (navigate: Navigate, href: string) => {
  const rootUrl = window.location.origin;
  return (
    !!navigate &&
    ((href.startsWith(rootUrl) &&
      !href.includes("proxy-api-request?redirect")) ||
      (href.startsWith("/") && !href.startsWith("/content/")))
  );
};
